import { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { MessageList } from './MessageList.js';
import { ChatInput } from './ChatInput.js';
import { Separator } from '@/components/ui/separator.js';
import { useChatStore } from '@/stores/chatStore.js';
import { useServices } from '@/contexts/ServicesContext.js';
import { useSessionStore } from '@/stores/sessionStore.js';
import { useChatTabsStore } from '@/stores/chatTabsStore.js';
import type { Contact } from '@/types/contact.js';
import type { Message } from '@/types/message.js';

// Empty array constant to avoid creating new references
const EMPTY_ARRAY: Message[] = [];

export function ChatArea() {
  // Get sessionId from URL to detect which session we're navigating to
  const { sessionId: urlSessionId } = useParams<{ sessionId: string }>();
  const currentSessionId = useSessionStore(state => state.currentSessionId);
  const isCreatingSession = useSessionStore(state => state.isCreatingSession);
  const activeTabId = useChatTabsStore((state) => state.activeTabId);
  const tabs = useChatTabsStore((state) => state.tabs);
  const { chatService } = useServices();

  // Get the session ID from the active tab (overrides global currentSessionId)
  const activeTab = tabs.find((t) => t.id === activeTabId);
  const displaySessionId = activeTab?.sessionId || currentSessionId;

  // Also track the URL session for loading state (handles race condition during session switch)
  const targetSessionId = urlSessionId || displaySessionId;

  // Selector with explicit shallow comparison to ensure re-renders on message updates
  // We need to trigger re-renders when the messages array reference changes
  const messages = useChatStore((state) => {
    if (!displaySessionId) {
      return EMPTY_ARRAY;
    }
    const sessionState = state.sessions.get(displaySessionId);
    if (!sessionState || !sessionState.messages) {
      return EMPTY_ARRAY;
    }

    // Return the messages array - Zustand will detect reference changes
    return sessionState.messages;
  });

  const processing = useChatStore((state) => {
    if (!displaySessionId) return false;
    return state.sessions.get(displaySessionId)?.processing || false;
  });

  const loadingHistory = useChatStore((state) => {
    // Check both target (URL) and display session for loading state
    // This handles the race condition when switching sessions
    if (targetSessionId && state.sessions.get(targetSessionId)?.loadingHistory) {
      return true;
    }
    if (displaySessionId && state.sessions.get(displaySessionId)?.loadingHistory) {
      return true;
    }
    // Also show loading if we're navigating to a session that hasn't loaded yet
    // (URL has sessionId but session state doesn't exist or has no messages)
    if (urlSessionId && !isCreatingSession) {
      const sessionState = state.sessions.get(urlSessionId);
      if (!sessionState || sessionState.messages.length === 0) {
        return true;
      }
    }
    return false;
  });

  // Show thinking indicator when creating session or processing
  const isThinking = isCreatingSession || processing;

  const messageCountRef = useRef(0);

  useEffect(() => {
    messageCountRef.current = messages.length;
  }, [messages.length]);

  const handleSend = async (
    message: string,
    imageData?: { base64: string; mimeType: string },
    fileData?: { base64: string; mimeType: string; filename?: string },
    mentionedContacts?: Contact[]
  ) => {
    try {
      await chatService.sendMessage(message, imageData, fileData, mentionedContacts);
    } catch (error) {
      console.error('Failed to send message:', error);
    }
  };

  return (
    <div className="flex h-full flex-col bg-background">
      {/* Message list takes up available space */}
      <div className="flex-1 overflow-hidden">
        <MessageList messages={messages} loading={isThinking} loadingHistory={loadingHistory} />
      </div>

      <Separator className="border-border/50" />

      {/* Chat input fixed at bottom */}
      <div className="bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
        <ChatInput onSend={handleSend} loading={isThinking} />
      </div>
    </div>
  );
}
